import { cpfValido, onlyDefined } from '@acme/common'
import { CsvMapper } from '@acme/core'
import dayjs from 'dayjs'
import { match } from 'oxide.ts'
import { Funcionario } from 'service/api'
import * as XLSX from 'xlsx'
import { TFuncionario } from '../store/types'

class FuncionarioMapper extends CsvMapper<
    Partial<Funcionario & { cargo: string }>
> {}

class LineError extends Error {
    constructor(
        public readonly campo: string,
        public readonly tipo: 'obrigatório' | 'inválido',
        public readonly linha: number,
        public readonly id: number,
    ) {
        super(`O campo ${campo} é ${tipo} na linha ${linha}.`)
    }
}

type ResFuncionarios = {
    data: Array<TFuncionario | undefined>
    erros: LineError[]
}

function ex(
    campo: string,
    tipo: typeof LineError.prototype.tipo,
    linha: number,
    id: number,
    erros?: LineError[],
) {
    if (erros) {
        if (!erros.find(e => e.campo === campo && e.linha === linha))
            erros.push(new LineError(campo, tipo, linha, id))
    } else throw new LineError(campo, tipo, linha, id)
}

function rmMask(cpf: string) {
    return cpf.replace(/\D/g, '')
}

const formatosDatas = [
    'YYYY-MM-DDTHH:mm:ss.SSSZ',
    'DD/MM/YYYY',
    'DD/MM/YY',
    'DD-MM-YYYY',
    'YYYY-MM-DD',
    'YYYYMMDD',
    'D/M/YYYY',
    'D/MM/YYYY',
    'DD/M/YYYY',
    'D/M/YY',
    'D/MM/YY',
    'DD/M/YY',
]

export function lerXlsFuncionarios(
    data: string | ArrayBuffer,
    linhaInicio = 1,
): ResFuncionarios {
    const erros: LineError[] = []

    const workbook = XLSX.read(data, { type: 'buffer', dateNF: 'dd/mm/yyyy' })
    const funcionariosData = XLSX.utils.sheet_to_json(
        workbook.Sheets[workbook.SheetNames[0]],
        {
            raw: false,
            header: linhaInicio,
            range: 1,
        },
    )

    return {
        data: funcionariosData
            .map((funcionario: any, index: number) => {
                if (!funcionario.length) return
                const linha = index + 1 + linhaInicio
                const id = index + 1

                let matricula = funcionario.at(0)
                const nome = funcionario.at(1)
                const cargo = funcionario.at(2)
                let pis = funcionario.at(3)
                let cpf = funcionario.at(4)
                let dataAdmissao = funcionario.at(5)

                if (!matricula && !nome && !pis && !cpf && !dataAdmissao) {
                    return undefined
                }

                matricula = String(matricula ?? '')
                    .trim()
                    .replace(/\D/g, '')

                if (!matricula) {
                    ex('Matrícula', 'obrigatório', linha, id, erros)
                }
                if (!String(nome ?? '').trim()) {
                    ex('Nome', 'obrigatório', linha, id, erros)
                }
                if (!String(pis ?? '').trim()) {
                    ex('PIS/PASEP', 'obrigatório', linha, id, erros)
                }
                if (!String(cpf ?? '').trim()) {
                    ex('CPF', 'obrigatório', linha, id, erros)
                }
                dataAdmissao = String(dataAdmissao ?? '').trim()
                if (!dataAdmissao) {
                    ex('Data de admissão', 'obrigatório', linha, id, erros)
                }

                cpf = rmMask(cpf ?? '').padStart(11, '0')
                pis = String(rmMask(pis ?? '').padStart(11, '0'))

                try {
                    dataAdmissao = dayjs(dataAdmissao, formatosDatas)
                    if (!dataAdmissao.isValid()) throw new Error()
                } catch (_e) {
                    ex('Data de admissão', 'inválido', linha, id, erros)
                    dataAdmissao = dayjs()
                }

                if (!cpfValido(cpf)) {
                    ex('CPF', 'inválido', linha, id, erros)
                }

                return {
                    id,
                    matricula: Number(matricula),
                    nome,
                    cargo,
                    pis,
                    cpf,
                    dtAdmissao: dataAdmissao.toISOString(),
                    dataAbnt: dataAdmissao.format('DD/MM/YYYY'),
                }
            })
            .filter(onlyDefined),
        erros,
    }
}

export function lerCsvFuncionarios(
    data: string | ArrayBuffer,
): ResFuncionarios {
    const erros: LineError[] = []

    const decoder = new TextDecoder('utf-8')
    data = decoder.decode(new Uint8Array(data as ArrayBuffer))

    const mapper = FuncionarioMapper.from(data, ({ CsvNumber, CsvString }) => ({
        matricula: CsvNumber,
        nome: CsvString,
        funcao: CsvString,
        pis: CsvString,
        cpf: CsvString,
        dtAdmissao: CsvString,
    }))

    const funcionariosData = match(mapper, {
        Ok: funcionarios => ({
            data: funcionarios.toPlain().map((funcionario, i) => {
                const index = i + 1

                if (!funcionario) return
                ;['matricula', 'nome', 'pis', 'cpf', 'dtAdmissao'].forEach(
                    campo => {
                        if (!(funcionario as any)[campo])
                            ex(campo, 'obrigatório', index, index, erros)
                    },
                )

                const [cpf, pis] = [
                    rmMask(String(funcionario.cpf)).padStart(11, '0'),
                    rmMask(String(funcionario.pis)).padStart(11, '0'),
                ]

                let dataAdmissao: any = String(funcionario.dtAdmissao).trim()
                try {
                    dataAdmissao = dayjs(dataAdmissao, formatosDatas)
                    if (!dataAdmissao.isValid()) throw new Error()
                } catch (_e) {
                    ex('Data de admissão', 'inválido', index, index, erros)
                    dataAdmissao = dayjs()
                }

                if (!cpfValido(cpf)) {
                    ex('CPF', 'inválido', index, index, erros)
                }

                return {
                    ...funcionario,
                    cargo: (funcionario as any).funcao,
                    id: index,
                    cpf,
                    pis,
                    dtAdmissao: dataAdmissao.toDate(),
                    dataAbnt: dataAdmissao.format('DD/MM/YYYY'),
                }
            }),
            erros,
        }),
        Err: err => {
            throw err
        },
    })

    return funcionariosData as ResFuncionarios
}
